'use client'

import { FC, useState, useTransition } from 'react'
import { NextIntlClientProvider, useTranslations } from 'next-intl'
import CategoryChips from '@/app/components/blog/CategoryChips'
import BlogPostsListWrapper from '@/app/components/blog/BlogPostsListWrapper'
import { BlogPost, Category } from '@/app/types/blog'
import { BLOG_POSTS_PER_PAGE } from '@/app/constants'
import Title from '../common/Title'

interface Props {
	categories: Category[]
	initialCategory: string
	initialPosts: BlogPost[]
	initialTotal: number
	language: string
	baseBlogPath: string
	messages: Record<string, any>
	resetChipsScrollOnMount?: boolean
}

const CategoryPageClient: FC<Props> = ({
	categories,
	initialCategory,
	initialPosts,
	initialTotal,
	language,
	baseBlogPath,
	messages,
	resetChipsScrollOnMount = false,
}) => {
	const t = useTranslations('Blog')
	const [activeCategory, setActiveCategory] = useState<string>(initialCategory)
	const [posts, setPosts] = useState<BlogPost[]>(initialPosts)
	const [total, setTotal] = useState<number>(initialTotal)
	const [isPending, startTransition] = useTransition()
	const [error, setError] = useState<string | null>(null)

	const currentCategory = categories.find(
		(category) => category.slug.current === activeCategory
	)

	const handleCategorySelect = (categorySlug: string) => {
		if (categorySlug === activeCategory || isPending) {
			return
		}

		startTransition(async () => {
			setError(null)

			const url = new URL('/api/blog/posts', window.location.origin)
			url.searchParams.set('language', language)
			url.searchParams.set('offset', '0')
			url.searchParams.set('limit', String(BLOG_POSTS_PER_PAGE))
			if (categorySlug !== 'all') {
				url.searchParams.set('categorySlug', categorySlug)
			}

			try {
				const response = await fetch(url.toString())
				if (!response.ok) {
					throw new Error('Failed to fetch blog posts')
				}

				const data = await response.json()
				setPosts(data.posts)
				setTotal(data.total)
				setActiveCategory(categorySlug)

				const targetPath =
					categorySlug === 'all'
						? `${baseBlogPath}/topics/all`
						: `${baseBlogPath}/topics/${categorySlug}`
				window.history.replaceState(null, '', targetPath)
			} catch (fetchError) {
				setError(t('errorLoading'))
			}
		})
	}

	const renderContent = () => {
		if (error) {
			return (
				<div className="blog-page__grid">
					<p className="text-red-500 text-center col-span-full py-12">{error}</p>
				</div>
			)
		}

		return (
			<BlogPostsListWrapper
				key={activeCategory}
				language={language}
				categorySlug={activeCategory === 'all' ? undefined : activeCategory}
				initialPosts={posts}
				initialTotal={total}
				messages={messages}
			/>
		)
	}

	return (
		<NextIntlClientProvider locale={language} messages={messages}>
			<div className="blog-page">
				<div className="blog-page__category-header">
					<Title level={1} bold color="text-900" className="blog-page__category-title" testId="blog-category-title">
						{activeCategory === 'all' ? t('allArticlesTitle') : currentCategory?.title}
					</Title>
				</div>

				<CategoryChips
					categories={categories}
					activeCategory={activeCategory}
					baseBlogPath={baseBlogPath}
					allLabel={t('allCategories')}
					onCategorySelect={handleCategorySelect}
					resetScrollOnMount={resetChipsScrollOnMount}
				/>

				{renderContent()}
			</div>
		</NextIntlClientProvider>
	)
}

export default CategoryPageClient

